import axios, { AxiosInstance, AxiosError, InternalAxiosRequestConfig } from 'axios'
import { toast } from 'sonner'
import { API_URL } from '../config/api'
import { isSpecDictatedUrl, keysToCamel, keysToSnake } from './caseConvert'

// Extend InternalAxiosRequestConfig to include _retry
declare module 'axios' {
  export interface InternalAxiosRequestConfig {
    _retry?: boolean
  }
}

// Create axios instance
export const apiClient: AxiosInstance = axios.create({
  baseURL: API_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Request interceptor:
//   1. Attach the bearer token from localStorage.
//   2. Convert request bodies + query params from camelCase
//      (TypeScript convention) to snake_case (Rust + Postgres
//      convention) for routes whose wire format is owned by
//      VynaHealth. Spec-dictated routes (FHIR, CDS Hooks,
//      OAuth, JWKS) bypass conversion — see
//      `isSpecDictatedUrl` for the list.
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = localStorage.getItem('access_token')
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`
    }

    if (!isSpecDictatedUrl(config.url)) {
      if (config.data && typeof config.data === 'object') {
        config.data = keysToSnake(config.data)
      }
      if (config.params && typeof config.params === 'object') {
        config.params = keysToSnake(config.params)
      }
    }

    return config
  },
  (error: AxiosError) => {
    return Promise.reject(error)
  }
)

// Response interceptor:
//   1. Convert response bodies from snake_case (wire) to
//      camelCase (TypeScript) for non-spec routes — same
//      opt-out as the request side so a single route either
//      converts on both legs or neither.
//   2. Handle 401 with a refresh-token retry.
//   3. Surface every other failure through `handleApiError`
//      as a toast.
apiClient.interceptors.response.use(
  (response) => {
    if (!isSpecDictatedUrl(response.config.url)) {
      if (response.data && typeof response.data === 'object') {
        response.data = keysToCamel(response.data)
      }
    }
    return response
  },
  async (error: AxiosError) => {
    const originalRequest = error.config

    // Handle 401 Unauthorized
    if (error.response?.status === 401 && originalRequest && !originalRequest._retry) {
      originalRequest._retry = true

      try {
        // Try to refresh token
        const refreshToken = localStorage.getItem('refresh_token')
        if (refreshToken) {
          const response = await axios.post(
            `${API_URL}/auth/refresh`,
            {},
            {
              headers: {
                Authorization: `Bearer ${refreshToken}`,
              },
            }
          )

          const { access_token } = response.data
          localStorage.setItem('access_token', access_token)

          // Retry original request with new token
          if (originalRequest.headers) {
            originalRequest.headers.Authorization = `Bearer ${access_token}`
          }
          return apiClient(originalRequest)
        }
      } catch (refreshError) {
        // Refresh failed, logout user
        localStorage.removeItem('access_token')
        localStorage.removeItem('refresh_token')
        window.location.href = '/login'
        return Promise.reject(refreshError)
      }
    }

    // Handle other errors
    handleApiError(error)
    return Promise.reject(error)
  }
)

// Error handler
function handleApiError(error: AxiosError) {
  if (error.response) {
    // Server responded with error
    const { status, data } = error.response
    
    switch (status) {
      case 400:
        toast.error('Invalid request. Please check your input.')
        break
      case 403:
        toast.error('You do not have permission to perform this action.')
        break
      case 404:
        toast.error('Resource not found.')
        break
      case 409:
        toast.error('Resource already exists.')
        break
      case 422:
        // Validation error
        if (data && typeof data === 'object' && 'error' in data) {
          const errorData = data as { error: { message: string } }
          toast.error(errorData.error.message || 'Validation error.')
        } else {
          toast.error('Validation error.')
        }
        break
      case 429:
        toast.error('Too many requests. Please try again later.')
        break
      case 500:
        toast.error('Server error. Please try again later.')
        break
      default:
        toast.error('An unexpected error occurred.')
    }
  } else if (error.request) {
    // Request made but no response
    toast.error('Network error. Please check your connection.')
  } else {
    // Error in request setup
    toast.error('An error occurred. Please try again.')
  }
}

// Portal-specific axios instance — reads JWT from portalAuthStore, not staff auth.
// Public portal endpoints (login, register) use apiClient; authenticated ones use this.
export const portalClient: AxiosInstance = axios.create({
  baseURL: API_URL,
  timeout: 30000,
  headers: { 'Content-Type': 'application/json' },
})

portalClient.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  // Read token from portalAuthStore's persisted state
  try {
    const raw = localStorage.getItem('vynahealth-portal-auth')
    if (raw) {
      const parsed = JSON.parse(raw)
      const token = parsed?.state?.token
      if (token && config.headers) {
        config.headers.Authorization = `Bearer ${token}`
      }
    }
  } catch { /* ignore parse errors */ }

  if (!isSpecDictatedUrl(config.url)) {
    if (config.data && typeof config.data === 'object') {
      config.data = keysToSnake(config.data)
    }
    if (config.params && typeof config.params === 'object') {
      config.params = keysToSnake(config.params)
    }
  }
  return config
})

portalClient.interceptors.response.use(
  (response) => {
    if (!isSpecDictatedUrl(response.config.url)) {
      if (response.data && typeof response.data === 'object') {
        response.data = keysToCamel(response.data)
      }
    }
    return response
  },
  async (error: AxiosError) => {
    if (error.response?.status === 401) {
      // Portal token expired — redirect to portal login
      localStorage.removeItem('vynahealth-portal-auth')
      if (window.location.pathname.startsWith('/portal')) {
        window.location.href = '/portal/login'
      }
    }
    handleApiError(error)
    return Promise.reject(error)
  }
)

// API helper functions with proper TypeScript types
import type {
  PaginatedResponse,
  Patient,
  PatientSearchParams,
  CreatePatientRequest,
  UpdatePatientRequest,
  EncounterResponse,
  EncounterSearchParams,
  CreateEncounterRequest,
  UpdateEncounterRequest,
  DischargeEncounterRequest,
  OrderResponse,
  OrderSearchParams,
  CreateOrderRequest,
  CancelOrderRequest,
  ResultResponse,
  ResultSearchParams,
  CreateResultRequest,
  AcknowledgeCriticalRequest,
  MedicationResponse,
  MedicationSearchParams,
  CreateMedicationRequest,
  PharmacistVerifyRequest,
  DiscontinueMedicationRequest,
  AppointmentSearchParams,
  CreateAppointmentRequest,
  CreateNoteRequest,
  ClinicalNoteResponse,
  ChargeResponse,
  ClaimResponse,
  PaymentResponse,
  PatientAccountResponse,
  DuplicateCandidate,
  ValidateIdResponse,
  MergePatientRequest,
  MergeRecordResponse,
  MergeHistoryResponse,
  HandoffResponse,
  CreateHandoffRequest,
  WorklistSummary,
  CreateNursingTaskRequest,
  CreateSpecimenRequest,
  SpecimenResponse,
  SpecimenTrackResponse,
  UpdateSpecimenStatusRequest,
  DischargeChecklistResponse,
  OrderSetResponse,
  OrderSetItemResponse,
  OrderSmartPhraseResponse,
  PendingCosignOrder,
  DeadLetterEntry,
  ProjectorLagResponse,
  ThirdPartyAppResponse,
  CreateThirdPartyAppRequest,
  UpdateThirdPartyAppRequest,
  ThirdPartyLaunchRequest,
  ThirdPartyLaunchResponse,
} from '../types/api'

export const api = {
  // Authentication
  auth: {
    login: (username: string, password: string, facilityId: number) =>
      apiClient.post('/auth/login', { username, password, facilityId }),
    logout: () => apiClient.post('/auth/logout'),
    refreshToken: () => apiClient.post('/auth/refresh'),
    getProfile: () => apiClient.get('/auth/profile'),
    updateProfile: (data: { fullName: string; email: string; phone?: string }) =>
      apiClient.put('/auth/profile', data),
    changePassword: (data: { currentPassword: string; newPassword: string }) =>
      apiClient.put('/auth/change-password', data),
    mfaSetup: () => apiClient.post('/auth/mfa/setup'),
    mfaVerifySetup: (data: { code: string }) => apiClient.post('/auth/mfa/verify-setup', data),
    updateAvatar: (avatarUrl: string) =>
      apiClient.put<{ avatarUrl: string }>('/auth/profile/avatar', { avatarUrl }),
    removeAvatar: () => apiClient.delete('/auth/profile/avatar'),
  },

  // Patients
  patients: {
    list: (params?: PatientSearchParams) =>
      apiClient.get<PaginatedResponse<Patient>>('/patients', { params }),
    get: (id: number) =>
      apiClient.get<Patient>(`/patients/${id}`),
    create: (data: CreatePatientRequest) =>
      apiClient.post<Patient>('/patients', data),
    update: (id: number, data: UpdatePatientRequest) =>
      apiClient.put<Patient>(`/patients/${id}`, data),
    delete: (id: number) =>
      apiClient.delete(`/patients/${id}`),
    checkDuplicate: (params: { fullName?: string; dateOfBirth?: string; nationalIdNumber?: string }) =>
      apiClient.get<DuplicateCandidate[]>('/patients/check-duplicate', { params }),
    validateId: (data: { idType: string; idValue: string }) =>
      apiClient.post<ValidateIdResponse>('/patients/validate-id', data),
    merge: (data: MergePatientRequest) =>
      apiClient.post<MergeRecordResponse>('/patients/merge', data),
    mergeHistory: (patientId: number) =>
      apiClient.get<MergeHistoryResponse[]>(`/patients/${patientId}/merge-history`),
    allergies: {
      list: (patientId: number) => apiClient.get(`/patients/${patientId}/allergies`),
      add: (patientId: number, data: { allergenName: string; allergyType: string; severity: string; reaction?: string; onsetDate?: string }) => apiClient.post(`/patients/${patientId}/allergies`, data),
      remove: (patientId: number, allergyId: number) => apiClient.delete(`/patients/${patientId}/allergies/${allergyId}`),
    },
    problems: {
      list: (patientId: number) => apiClient.get(`/patients/${patientId}/problems`),
      add: (patientId: number, data: { problemName: string; icdCode?: string; severity?: string; onsetDate?: string }) => apiClient.post(`/patients/${patientId}/problems`, data),
      update: (patientId: number, problemId: number, data: object) => apiClient.put(`/patients/${patientId}/problems/${problemId}`, data),
    },
    insurance: {
      list: (patientId: number) => apiClient.get(`/patients/${patientId}/insurance`),
      add: (patientId: number, data: { patientId: number; payerName: string; planName?: string; memberId: string; groupNumber?: string; effectiveDate?: string; expiryDate?: string; priority: string }) => apiClient.post(`/patients/${patientId}/insurance`, data),
    },
    immunizations: {
      list: (patientId: number) => apiClient.get(`/patients/${patientId}/immunizations`),
      add: (patientId: number, data: { patientId: number; vaccineName: string; dateAdministered: string; lotNumber?: string; site?: string; administeredBy?: string; nextDueDate?: string }) => apiClient.post(`/patients/${patientId}/immunizations`, data),
    },
    contacts: {
      list: (patientId: number) => apiClient.get(`/patients/${patientId}/contacts`),
      add: (patientId: number, data: object) => apiClient.post(`/patients/${patientId}/contacts`, data),
      update: (patientId: number, contactId: number, data: object) => apiClient.put(`/patients/${patientId}/contacts/${contactId}`, data),
      delete: (patientId: number, contactId: number) => apiClient.delete(`/patients/${patientId}/contacts/${contactId}`),
    },
    familyHistory: {
      list: (patientId: number) => apiClient.get(`/patients/${patientId}/family-history`),
      add: (patientId: number, data: object) => apiClient.post(`/patients/${patientId}/family-history`, data),
    },
    socialHistory: {
      get: (patientId: number) => apiClient.get(`/patients/${patientId}/social-history`),
      update: (patientId: number, data: object) => apiClient.put(`/patients/${patientId}/social-history`, data),
    },
    surgicalHistory: {
      list: (patientId: number) => apiClient.get(`/patients/${patientId}/surgical-history`),
      add: (patientId: number, data: object) => apiClient.post(`/patients/${patientId}/surgical-history`, data),
    },
    referrals: {
      list: (patientId: number) => apiClient.get(`/patients/${patientId}/referrals`),
      create: (patientId: number, data: object) => apiClient.post(`/patients/${patientId}/referrals`, data),
    },
    advanceDirectives: {
      list: (patientId: number) => apiClient.get(`/patients/${patientId}/advance-directives`),
      create: (patientId: number, data: object) => apiClient.post(`/patients/${patientId}/advance-directives`, data),
    },
    identifiers: {
      list: (patientId: number) => apiClient.get(`/patients/${patientId}/identifiers`),
      add: (patientId: number, data: object) => apiClient.post(`/patients/${patientId}/identifiers`, data),
      remove: (patientId: number, identifierId: number) => apiClient.delete(`/patients/${patientId}/identifiers/${identifierId}`),
    },
    addresses: {
      list: (patientId: number) => apiClient.get(`/patients/${patientId}/addresses`),
      add: (patientId: number, data: object) => apiClient.post(`/patients/${patientId}/addresses`, data),
      update: (patientId: number, addressId: number, data: object) => apiClient.put(`/patients/${patientId}/addresses/${addressId}`, data),
      remove: (patientId: number, addressId: number) => apiClient.delete(`/patients/${patientId}/addresses/${addressId}`),
    },
    carePlans: {
      list: (patientId: number) => apiClient.get(`/patients/${patientId}/care-plans`),
      add: (patientId: number, data: object) => apiClient.post(`/patients/${patientId}/care-plans`, data),
      addGoal: (patientId: number, carePlanId: number, data: object) => apiClient.post(`/patients/${patientId}/care-plans/${carePlanId}/goals`, data),
    },
  },

  // Encounters
  encounters: {
    list: (params?: EncounterSearchParams) =>
      apiClient.get<PaginatedResponse<EncounterResponse>>('/encounters', { params }),
    get: (id: number) =>
      apiClient.get<EncounterResponse>(`/encounters/${id}`),
    create: (data: CreateEncounterRequest) =>
      apiClient.post<EncounterResponse>('/encounters', data),
    update: (id: number, data: UpdateEncounterRequest) =>
      apiClient.put<EncounterResponse>(`/encounters/${id}`, data),
    delete: (id: number) =>
      apiClient.delete(`/encounters/${id}`),
    discharge: (id: number, data: DischargeEncounterRequest) =>
      apiClient.post<EncounterResponse>(`/encounters/${id}/discharge`, data),
    transfer: (encounterId: number, data: { toLocationId: number; transferReason: string }) =>
      apiClient.post(`/encounters/${encounterId}/transfer`, data),
    getTransferHistory: (encounterId: number) =>
      apiClient.get(`/encounters/${encounterId}/transfers`),
    // Vitals sub-resource (backend endpoint pending — returns 404 until implemented)
    vitals: {
      list: (encounterId: number) => apiClient.get(`/encounters/${encounterId}/vitals`),
      record: (encounterId: number, data: object) => apiClient.post(`/encounters/${encounterId}/vitals`, data),
    },
    // Intake/Output sub-resource (backend endpoint pending — returns 404 until implemented)
    io: {
      list: (encounterId: number) => apiClient.get(`/encounters/${encounterId}/io`),
      add: (encounterId: number, data: object) => apiClient.post(`/encounters/${encounterId}/io`, data),
    },
  },

  // Orders (CPOE)
  orders: {
    list: (params?: OrderSearchParams) =>
      apiClient.get<PaginatedResponse<OrderResponse>>('/orders', { params }),
    get: (id: number) =>
      apiClient.get<OrderResponse>(`/orders/${id}`),
    create: (data: CreateOrderRequest) =>
      apiClient.post<OrderResponse>('/orders', data),
    cancel: (id: number, data: CancelOrderRequest) =>
      apiClient.post<OrderResponse>(`/orders/${id}/cancel`, data),
    complete: (id: number) =>
      apiClient.post<OrderResponse>(`/orders/${id}/complete`),
    cosign: (id: number) =>
      apiClient.post(`/orders/${id}/cosign`),
    delete: (id: number) =>
      apiClient.delete(`/orders/${id}`),
  },

  // Results
  results: {
    list: (params?: ResultSearchParams) =>
      apiClient.get<PaginatedResponse<ResultResponse>>('/results', { params }),
    get: (id: number) =>
      apiClient.get<ResultResponse>(`/results/${id}`),
    create: (data: CreateResultRequest) =>
      apiClient.post<ResultResponse>('/results', data),
    acknowledge: (id: number, data: AcknowledgeCriticalRequest) =>
      apiClient.post<ResultResponse>(`/results/${id}/acknowledge`, data),
    delete: (id: number) =>
      apiClient.delete(`/results/${id}`),
  },

  // Medications
  medications: {
    list: (params?: MedicationSearchParams) =>
      apiClient.get<PaginatedResponse<MedicationResponse>>('/medications', { params }),
    get: (id: number) =>
      apiClient.get<MedicationResponse>(`/medications/${id}`),
    create: (data: CreateMedicationRequest) =>
      apiClient.post<MedicationResponse>('/medications', data),
    verify: (id: number, data: PharmacistVerifyRequest) =>
      apiClient.post<MedicationResponse>(`/medications/${id}/verify`, data),
    discontinue: (id: number, data: DiscontinueMedicationRequest) =>
      apiClient.post<MedicationResponse>(`/medications/${id}/discontinue`, data),
    delete: (id: number) =>
      apiClient.delete(`/medications/${id}`),
    administer: (medicationId: number, data: object) =>
      apiClient.post(`/medications/${medicationId}/administer`, data),
    listAdministrations: (medicationId: number, params?: object) =>
      apiClient.get(`/medications/${medicationId}/administrations`, { params }),
    marSchedule: (params: { patientId: number; date: string }) =>
      apiClient.get('/medications/mar/schedule', { params }),
    calculateDose: (data: { medicationName: string; weightKg: number; heightCm?: number; gfr?: number; bsa?: number }) =>
      apiClient.post('/medications/calculate-dose', data),
  },

  // Documentation
  documentation: {
    list: (params?: { patientId?: number; encounterId?: number; noteType?: string; status?: string; page?: number; perPage?: number }) =>
      apiClient.get<PaginatedResponse<ClinicalNoteResponse>>('/notes', { params }),
    get: (id: number) =>
      apiClient.get<ClinicalNoteResponse>(`/notes/${id}`),
    create: (data: CreateNoteRequest) =>
      apiClient.post<ClinicalNoteResponse>('/notes', data),
    update: (id: number, data: Partial<CreateNoteRequest>) =>
      apiClient.put<ClinicalNoteResponse>(`/notes/${id}`, data),
    sign: (id: number, data: { signedByUserId: number }) =>
      apiClient.post(`/notes/${id}/sign`, data),
    amend: (id: number, data: { amendmentReason: string; amendedByUserId: number }) =>
      apiClient.post(`/notes/${id}/amend`, data),
    cosign: (id: number, data: { cosignedByUserId: number }) =>
      apiClient.post(`/notes/${id}/cosign`, data),
    templates: {
      list: (params?: object) => apiClient.get('/notes/templates', { params }),
      get: (id: number) => apiClient.get(`/notes/templates/${id}`),
      create: (data: { templateName: string; templateType: string; specialty?: string; content: string; isActive?: boolean }) => apiClient.post('/notes/templates', data),
      update: (id: number, data: object) => apiClient.put(`/notes/templates/${id}`, data),
    },
    smartPhrases: {
      list: (params?: object) => apiClient.get('/notes/smart-phrases', { params }),
      create: (data: object) => apiClient.post('/notes/smart-phrases', data),
      expand: (data: { phraseKey: string; patientId?: number; encounterId?: number }) => apiClient.post('/notes/smart-phrases/expand', data),
    },
  },

  // Scheduling
  scheduling: {
    list: (params?: AppointmentSearchParams) => apiClient.get('/scheduling/appointments', { params }),
    get: (id: number) => apiClient.get(`/scheduling/appointments/${id}`),
    create: (data: CreateAppointmentRequest) => apiClient.post('/scheduling/appointments', data),
    update: (id: number, data: Partial<CreateAppointmentRequest>) => apiClient.put(`/scheduling/appointments/${id}`, data),
    cancel: (id: number, reason: string) => apiClient.post(`/scheduling/appointments/${id}/cancel`, { reason }),
    checkIn: (id: number) => apiClient.post(`/scheduling/appointments/${id}/check-in`),
    getSlots: (params: { providerId: number; dateStart: string; dateEnd: string }) => apiClient.get('/scheduling/slots', { params }),
    waitlist: (params?: object) => apiClient.get('/scheduling/waitlist', { params }),
  },

  // Billing
  billing: {
    charges: {
      list: (params?: object) => apiClient.get<PaginatedResponse<ChargeResponse>>('/billing/charges', { params }),
      create: (data: { encounterId: number; chargeCode: string; description: string; quantity: number; unitPrice: number; serviceDate: string }) => apiClient.post<ChargeResponse>('/billing/charges', data),
    },
    claims: {
      list: (params?: object) => apiClient.get<PaginatedResponse<ClaimResponse>>('/billing/claims', { params }),
      create: (data: object) => apiClient.post<ClaimResponse>('/billing/claims', data),
      submit: (id: number) => apiClient.post<ClaimResponse>(`/billing/claims/${id}/submit`),
    },
    payments: {
      list: (params?: object) => apiClient.get<PaginatedResponse<PaymentResponse>>('/billing/payments', { params }),
      create: (data: { claimId?: number; patientId: number; amount: number; paymentMethod: string; referenceNumber?: string; notes?: string }) => apiClient.post<PaymentResponse>('/billing/payments', data),
    },
    accounts: {
      get: (patientId: number) => apiClient.get<PatientAccountResponse>(`/billing/accounts/${patientId}`),
    },
    statements: {
      generate: (data: object) => apiClient.post('/billing/statements', data),
    },
    denials: {
      list: (params?: object) => apiClient.get('/billing/denials', { params }),
      updateStatus: (id: number, data: object) => apiClient.put(`/billing/denials/${id}`, data),
    },
  },

  // Notifications
  notifications: {
    list: (params?: object) => apiClient.get('/notifications', { params }),
    send: (data: object) => apiClient.post('/notifications', data),
    markRead: (id: number) => apiClient.put(`/notifications/${id}/read`),
    markAllRead: () => apiClient.post('/notifications/read-all'),
    getPreferences: () => apiClient.get('/notifications/preferences'),
    updatePreference: (id: number, data: object) => apiClient.put(`/notifications/preferences/${id}`, data),
  },

  // Analytics
  analytics: {
    runReport: (data: object) => apiClient.post('/analytics/reports/run', data),
    listReports: () => apiClient.get('/analytics/reports'),
    dashboards: {
      list: () => apiClient.get('/analytics/dashboards'),
      get: (id: number) => apiClient.get(`/analytics/dashboards/${id}`),
      create: (data: object) => apiClient.post('/analytics/dashboards', data),
      update: (id: number, data: object) => apiClient.put(`/analytics/dashboards/${id}`, data),
      delete: (id: number) => apiClient.delete(`/analytics/dashboards/${id}`),
    },
    qualityMeasures: (params?: object) => apiClient.get('/analytics/quality-measures', { params }),
  },

  // Clinical Decision Support
  cds: {
    evaluate: (data: object) => apiClient.post('/cds/evaluate', data),
    checkInteractions: (data: object) => apiClient.post('/cds/drug-interactions', data),
    checkAllergies: (data: object) => apiClient.post('/cds/drug-allergies', data),
    alerts: {
      list: (params?: object) => apiClient.get('/cds/alerts', { params }),
      acknowledge: (id: number, userId: number) => apiClient.post(`/cds/alerts/${id}/acknowledge`, { acknowledgedByUserId: userId }),
      override: (id: number, data: object) => apiClient.post(`/cds/alerts/${id}/override`, data),
    },
    rules: {
      list: () => apiClient.get('/cds/rules'),
      create: (data: object) => apiClient.post('/cds/rules', data),
      update: (id: number, data: object) => apiClient.put(`/cds/rules/${id}`, data),
    },
  },

  // Terminology
  terminology: {
    search: (params: { system?: string; display?: string; limit?: number }) =>
      apiClient.get('/fhir/r4/CodeSystem/$search', { params }),
  },

  // FHIR R4
  fhir: {
    getResource: (type: string, id: string) => apiClient.get(`/fhir/r4/${type}/${id}`),
    searchResources: (type: string, params?: Record<string, string>) => apiClient.get(`/fhir/r4/${type}`, { params }),
    createResource: (type: string, data: object) => apiClient.post(`/fhir/r4/${type}`, data),
    patientEverything: (id: string) => apiClient.get(`/fhir/r4/Patient/${id}/$everything`),
    bulkExport: (data: object) => apiClient.post('/fhir/$export', data),
  },

  // Patient Portal — public endpoints use apiClient, authenticated use portalClient
  portal: {
    login: (data: { email: string; password: string }) => apiClient.post('/portal/login', data),
    register: (data: { email: string; password: string; fullName: string; dateOfBirth: string; phone?: string; mrn?: string; nationalIdType?: string; nationalIdNumber?: string }) =>
      apiClient.post('/portal/register', data),
    getUser: () => portalClient.get('/portal/user'),
    updateUser: (data: { fullName?: string; phone?: string; preferredLanguage?: string }) =>
      portalClient.put('/portal/user', data),
    messages: {
      list: (params?: object) => portalClient.get('/portal/messages', { params }),
      send: (data: { subject: string; body: string; threadId?: number; recipientProviderId?: number; recipientDepartmentId?: number; category?: string; priority?: string }) =>
        portalClient.post('/portal/messages', data),
    },
    summary: () => portalClient.get('/portal/summary'),
    appointments: (params?: object) => portalClient.get('/portal/appointments', { params }),
    results: (params?: object) => portalClient.get('/portal/results', { params }),
    forms: {
      list: () => portalClient.get('/portal/forms'),
      submit: (data: { formId: number; responses: Record<string, unknown> }) =>
        portalClient.post('/portal/forms', data),
    },
  },

  // Providers
  providers: {
    list: (params?: object) => apiClient.get('/providers', { params }),
    get: (id: number) => apiClient.get(`/providers/${id}`),
  },

  // Configuration
  config: {
    list: () => apiClient.get('/config').then(r => r.data),
    get: (key: string) => apiClient.get(`/config/${key}`).then(r => r.data),
    update: (key: string, value: string) => apiClient.put(`/config/${key}`, { value }).then(r => r.data),
    getFacilityOverrides: (facilityId: number) => apiClient.get(`/config/facility/${facilityId}`).then(r => r.data),
    setFacilityOverride: (facilityId: number, key: string, value: string) =>
        apiClient.put(`/config/facility/${facilityId}/${key}`, { value }).then(r => r.data),
    getUserPreferences: () => apiClient.get('/config/user/preferences').then(r => r.data),
    setUserPreference: (key: string, value: string) =>
        apiClient.put(`/config/user/preferences/${key}`, { value }).then(r => r.data),
  },

  // Break-Glass Access
  breakGlass: {
    create: (data: { patientId: number; justification: string }) =>
      apiClient.post('/break-glass', data),
  },

  // RBAC
  rbac: {
    // Categories
    listCategories: () => apiClient.get('/rbac/categories').then(r => r.data),
    createCategory: (data: object) => apiClient.post('/rbac/categories', data).then(r => r.data),
    getCategory: (id: number) => apiClient.get(`/rbac/categories/${id}`).then(r => r.data),
    updateCategory: (id: number, data: object) => apiClient.put(`/rbac/categories/${id}`, data).then(r => r.data),
    deleteCategory: (id: number) => apiClient.delete(`/rbac/categories/${id}`),
    getCategoryPermissions: (id: number) => apiClient.get(`/rbac/categories/${id}/permissions`).then(r => r.data),
    setCategoryPermissions: (id: number, permissionIds: number[]) => apiClient.put(`/rbac/categories/${id}/permissions`, { permissionIds }).then(r => r.data),

    // Roles
    listRoles: () => apiClient.get('/rbac/roles').then(r => r.data),
    createRole: (data: object) => apiClient.post('/rbac/roles', data).then(r => r.data),
    getRole: (id: number) => apiClient.get(`/rbac/roles/${id}`).then(r => r.data),
    updateRole: (id: number, data: object) => apiClient.put(`/rbac/roles/${id}`, data).then(r => r.data),
    deleteRole: (id: number) => apiClient.delete(`/rbac/roles/${id}`),
    getRolePermissions: (id: number) => apiClient.get(`/rbac/roles/${id}/permissions`).then(r => r.data),
    setRolePermissions: (id: number, permissionIds: number[]) => apiClient.put(`/rbac/roles/${id}/permissions`, { permissionIds }).then(r => r.data),
    getEffectivePermissions: (id: number) => apiClient.get(`/rbac/roles/${id}/effective`).then(r => r.data),
    getDeniedPermissions: (id: number) => apiClient.get(`/rbac/roles/${id}/denied`).then(r => r.data),
    setDeniedPermissions: (id: number, items: object[]) => apiClient.put(`/rbac/roles/${id}/denied`, { denied: items }).then(r => r.data),

    // Permissions
    listPermissions: () => apiClient.get('/rbac/permissions').then(r => r.data),
    createPermission: (data: object) => apiClient.post('/rbac/permissions', data).then(r => r.data),
    updatePermission: (id: number, data: object) => apiClient.put(`/rbac/permissions/${id}`, data).then(r => r.data),
    deletePermission: (id: number) => apiClient.delete(`/rbac/permissions/${id}`),

    // Matrix
    getMatrix: () => apiClient.get('/rbac/matrix').then(r => r.data),

    // User roles
    getUserRoles: (userId: number) => apiClient.get(`/rbac/users/${userId}/roles`).then(r => r.data),
    assignUserRole: (userId: number, data: object) => apiClient.post(`/rbac/users/${userId}/roles`, data).then(r => r.data),
    removeUserRole: (userId: number, roleId: number) => apiClient.delete(`/rbac/users/${userId}/roles/${roleId}`),
    getUserPermissions: (userId: number) => apiClient.get(`/rbac/users/${userId}/permissions`).then(r => r.data),
  },

  // Admin / User & Role Management (delegates to /rbac/ endpoints)
  admin: {
    users: {
      // No /rbac/users list endpoint exists yet; user CRUD is pending backend implementation.
      list: (params?: object) => apiClient.get('/rbac/users', { params }),
      get: (id: number) => apiClient.get(`/rbac/users/${id}`),
      create: (data: object) => apiClient.post('/rbac/users', data),
      update: (id: number, data: object) => apiClient.put(`/rbac/users/${id}`, data),
      delete: (id: number) => apiClient.delete(`/rbac/users/${id}`),
    },
    roles: {
      list: () => apiClient.get('/rbac/roles'),
      create: (data: object) => apiClient.post('/rbac/roles', data),
      update: (id: number, data: object) => apiClient.put(`/rbac/roles/${id}`, data),
    },
  },

  // Protocols
  protocols: {
    list: () => apiClient.get('/clinical-workflows/protocols'),
    enroll: (data: { protocolId: number; encounterId: number; patientId: number }) =>
      apiClient.post('/clinical-workflows/protocols/enroll', data),
    enrollments: (params?: object) =>
      apiClient.get('/clinical-workflows/protocols/enrollments', { params }),
    advanceStep: (id: number) =>
      apiClient.post(`/clinical-workflows/protocols/enrollments/${id}/advance`),
  },

  // R-1.1: Handoff / I-PASS
  handoffs: {
    listPending: () =>
      apiClient.get<HandoffResponse[]>('/clinical/handoffs'),
    create: (data: CreateHandoffRequest) =>
      apiClient.post<HandoffResponse>('/clinical/handoffs', data),
    acknowledge: (id: number, data: { synthesis?: string }) =>
      apiClient.post(`/clinical/handoffs/${id}/acknowledge`, data),
  },

  // R-1.2: Nursing task queue
  nursing: {
    worklist: () =>
      apiClient.get<WorklistSummary>('/clinical/nursing/worklist'),
    createTask: (data: CreateNursingTaskRequest) =>
      apiClient.post('/clinical/nursing/tasks', data),
    completeTask: (id: number) =>
      apiClient.post(`/clinical/nursing/tasks/${id}/complete`),
  },

  // R-1.3: Specimen tracking
  specimens: {
    create: (data: CreateSpecimenRequest) =>
      apiClient.post<SpecimenResponse>('/clinical/specimens', data),
    updateStatus: (id: number, data: UpdateSpecimenStatusRequest) =>
      apiClient.put(`/clinical/specimens/${id}/status`, data),
    track: (barcode: string) =>
      apiClient.get<SpecimenTrackResponse>(`/clinical/specimens/track/${barcode}`),
  },

  // R-1.4: Discharge checklist
  discharge: {
    getChecklist: (encounterId: number) =>
      apiClient.get<DischargeChecklistResponse>(`/clinical/discharge/${encounterId}/checklist`),
    completeStep: (encounterId: number, step: string) =>
      apiClient.post(`/clinical/discharge/${encounterId}/checklist/${step}`),
  },

  // R-1.5: Order sets + smart phrases
  orderSets: {
    list: (params?: { category?: string; specialty?: string; q?: string }) =>
      apiClient.get<OrderSetResponse[]>('/clinical/order-sets', { params }),
    get: (id: number) =>
      apiClient.get<OrderSetResponse>(`/clinical/order-sets/${id}`),
    getItems: (id: number) =>
      apiClient.get<OrderSetItemResponse[]>(`/clinical/order-sets/${id}/items`),
  },

  smartPhrases: {
    list: (params?: { category?: string; q?: string }) =>
      apiClient.get<OrderSmartPhraseResponse[]>('/clinical/smart-phrases', { params }),
    expand: (code: string) =>
      apiClient.get<OrderSmartPhraseResponse>(`/clinical/smart-phrases/${code}`),
  },

  // R-0.5: Pending cosign queue
  cosign: {
    pendingOrders: () =>
      apiClient.get<PendingCosignOrder[]>('/orders/pending-cosign'),
  },

  // CCOW context management
  context: {
    get: () => apiClient.get('/context/get'),
    set: (data: { patientId?: number; encounterId?: number; facilityId?: number }) =>
      apiClient.post('/context/set', data),
    join: (data: { applicationName: string; callbackUrl?: string }) =>
      apiClient.post('/context/join', data),
    leave: (data: { applicationName: string }) =>
      apiClient.post('/context/leave', data),
    survey: (data: { patientId?: number; encounterId?: number; facilityId?: number }) =>
      apiClient.post('/context/survey', data),
    commit: () => apiClient.post('/context/commit'),
    cancel: () => apiClient.post('/context/cancel'),
  },

  // Third-party app configuration + launch
  thirdPartyApps: {
    list: (params?: { displayInChart?: boolean; displayInSidebar?: boolean; appCategory?: string; isActive?: boolean }) =>
      apiClient.get<ThirdPartyAppResponse[]>('/third-party-apps', { params }),
    get: (id: number) =>
      apiClient.get<ThirdPartyAppResponse>(`/third-party-apps/${id}`),
    create: (data: CreateThirdPartyAppRequest) =>
      apiClient.post<ThirdPartyAppResponse>('/third-party-apps', data),
    update: (id: number, data: UpdateThirdPartyAppRequest) =>
      apiClient.put<ThirdPartyAppResponse>(`/third-party-apps/${id}`, data),
    deactivate: (id: number) =>
      apiClient.delete(`/third-party-apps/${id}`),
    launch: (appCode: string, data: ThirdPartyLaunchRequest) =>
      apiClient.post<ThirdPartyLaunchResponse>(`/third-party-apps/${appCode}/launch`, data),
  },

  // R-3.4 + R-3.5: Admin health
  adminHealth: {
    deadLetters: () =>
      apiClient.get<DeadLetterEntry[]>('/admin/outbox/dead-letter'),
    requeueDeadLetter: (id: number) =>
      apiClient.post(`/admin/outbox/dead-letter/${id}/requeue`),
    projectorLag: () =>
      apiClient.get<ProjectorLagResponse[]>('/admin/health/projector-lag'),
  },
}

export default api
